import React, { useCallback, useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { schoolApi, showTime } from '../../common/schoolApi';

/**
 * WiZi staff adding staff to a school.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * Provisioning builds a school's database and not one user, so a freshly
 * provisioned school has nobody who can sign in. The school's own Teachers
 * screen cannot fix that: it is reached by a principal, and the principal is
 * the person who does not exist yet. Until now the only way through was
 * bin/add_school_user.php over SSH.
 *
 * So this is that CLI script as a screen, and it answers to a super
 * administrator. It is the only place in the panel that can create a school
 * ADMINISTRATOR -- /api/school_teacher hardcodes the teacher role, deliberately,
 * because a customer must not be able to mint administrators of their own.
 *
 * THE SCHOOL IS PICKED HERE, AND THAT IS THE POINT
 * ------------------------------------------------
 * Everywhere else in the product, naming the school in a request is the bug --
 * a school session takes it from the signed token so a principal cannot answer
 * with a rival's. Here the caller is WiZi, has no school binding to take it
 * from, and choosing the school is the whole job. The API makes the same
 * distinction: /api/school_member is behind require_super_admin(), not
 * require_school_role().
 *
 * THE PASSWORD IS SHOWN ONCE
 * --------------------------
 * Nothing stores it in plain text, so the dialog after a successful add is the
 * only time it will ever be readable. It stays open until it is dismissed on
 * purpose, and it says so -- a toast that fades after four seconds would lose
 * the one thing the operator came for.
 *
 * @see api/application/controllers/api/School_member.php
 * @see api/bin/add_school_user.php
 */

type Member = {
    user_id: number;
    username: string;
    full_name: string;
    role: 'admin' | 'teacher';
    role_label: string;
    status: string;
    must_change_password: boolean;
    joined_at: string | null;
    left_at: string | null;
};

type School = { id: number; code: string; name: string; status: string; db_status: string | null };

type Form = {
    role: 'admin' | 'teacher';
    username: string;
    full_name: string;
    password: string;
    email: string;
    phone: string;
    employee_no: string;
};

const emptyForm = (role: 'admin' | 'teacher'): Form => ({
    role,
    username: '',
    full_name: '',
    password: '',
    email: '',
    phone: '',
    employee_no: '',
});

/** The password panel. Shown after an add and after a reset; dismissed by hand. */
const PasswordOnce = ({
    password,
    username,
    onClose,
}: {
    password: string;
    username: string;
    onClose: () => void;
}) => {
    const notify = useNotify();

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(`${username} / ${password}`);
            notify('Copied', { type: 'info' });
        } catch {
            // Clipboard access is blocked on plain http and in some browsers.
            // The password is on screen and selectable, so this is a missing
            // convenience rather than a missing result.
            notify('Could not copy. Select the text instead.', { type: 'warning' });
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Give them these details</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>This is the only time this password is readable</AlertTitle>
                    It is not stored in plain text anywhere and cannot be looked up again &mdash;
                    only reset. They must change it at their first sign-in.
                </Alert>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                                Login
                            </Typography>
                            <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                Password
                            </Typography>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                                {password}
                            </Typography>
                        </Box>
                        <Tooltip title="Copy login and password">
                            <IconButton onClick={copy}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Paper>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>
                    I have saved it
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const SchoolMembers = () => {
    const notify = useNotify();

    const [schools, setSchools] = useState<School[]>([]);
    const [schoolId, setSchoolId] = useState<number | ''>('');
    const [members, setMembers] = useState<Member[]>([]);
    const [includeLeft, setIncludeLeft] = useState(false);

    const [loadingSchools, setLoadingSchools] = useState(true);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState<Form | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [secret, setSecret] = useState<{ username: string; password: string } | null>(null);
    const [confirmRemove, setConfirmRemove] = useState<Member | null>(null);

    const school = schools.find(s => s.id === schoolId) || null;

    // --- the school list ---------------------------------------------------
    useEffect(() => {
        (async () => {
            try {
                const res = await schoolApi.get<School[]>('school', { pagesize: 200, page: 1, sortby: 'name' });
                setSchools(res.result || []);
            } catch (e: any) {
                notify(e.message || 'Could not load the schools.', { type: 'error' });
            } finally {
                setLoadingSchools(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- the staff of the chosen school ------------------------------------
    const load = useCallback(async () => {
        if (!schoolId) {
            setMembers([]);
            return;
        }
        setLoading(true);
        try {
            const res = await schoolApi.get<{ members: Member[] }>('school_member', {
                school_id: schoolId,
                include_left: includeLeft ? 1 : '',
            });
            setMembers(res.result.members || []);
        } catch (e: any) {
            setMembers([]);
            notify(e.message || 'Could not load the staff list.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [schoolId, includeLeft, notify]);

    useEffect(() => {
        load();
    }, [load]);

    // --- writes ------------------------------------------------------------
    const save = async () => {
        if (!form || !schoolId) return;

        if (!form.username.trim() || !form.full_name.trim()) {
            setFormError('A login and a full name are both required.');
            return;
        }

        setSaving(true);
        setFormError(null);
        try {
            const res = await schoolApi.post('school_member', {
                school_id: schoolId,
                role: form.role,
                username: form.username.trim(),
                full_name: form.full_name.trim(),
                // Empty means "generate one", which is the better default: a
                // password typed by an operator is a password they will reuse.
                password: form.password.trim() || undefined,
                email: form.email.trim() || undefined,
                phone: form.phone.trim() || undefined,
                employee_no: form.employee_no.trim() || undefined,
            });

            const username = form.username.trim();
            setForm(null);
            notify(res.message, { type: 'success' });
            (res.result.warnings || []).forEach((w: string) => notify(w, { type: 'warning' }));

            if (res.result.password_once) {
                setSecret({ username, password: res.result.password_once });
            }
            load();
        } catch (e: any) {
            // The API's messages are written to be read ("This login is already
            // registered on WiZi..."), so they are shown as they are, in the
            // form, next to the field that caused them.
            setFormError(e.message || 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async (m: Member) => {
        try {
            const res = await schoolApi.post(`school_member/reset_password/${m.user_id}`, {
                school_id: schoolId,
            });
            notify(res.message, { type: 'success' });
            if (res.result.password_once) {
                setSecret({ username: m.username, password: res.result.password_once });
            }
            load();
        } catch (e: any) {
            notify(e.message || 'Could not reset the password.', { type: 'error' });
        }
    };

    const remove = async (m: Member) => {
        setConfirmRemove(null);
        try {
            const res = await schoolApi.post(`school_member/remove/${m.user_id}`, {
                school_id: schoolId,
            });
            notify(res.message, { type: 'success' });
            load();
        } catch (e: any) {
            notify(e.message || 'Could not remove them.', { type: 'error' });
        }
    };

    const admins = members.filter(m => m.role === 'admin' && m.status === 'active').length;
    const teachers = members.filter(m => m.role === 'teacher' && m.status === 'active').length;
    const notServing = !!school && school.db_status !== 'active';

    return (
        <Card sx={{ mt: 2 }}>
            <Title title="School staff" />
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Add a principal or a teacher to a school
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    A newly provisioned school has a database and no people in it. Add its
                    principal here; they then add their own teachers from their side of the
                    product. Students are loaded by the school through Import a roster, which is
                    where the class sections are.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <TextField
                        select
                        label="School"
                        value={schoolId}
                        onChange={e => setSchoolId(e.target.value === '' ? '' : Number(e.target.value))}
                        sx={{ minWidth: 320 }}
                        disabled={loadingSchools}
                        helperText={loadingSchools ? 'Loading schools…' : ' '}
                    >
                        <MenuItem value="">
                            <em>Choose a school</em>
                        </MenuItem>
                        {schools.map(s => (
                            <MenuItem key={s.id} value={s.id}>
                                {s.code} &mdash; {s.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeLeft}
                                onChange={e => setIncludeLeft(e.target.checked)}
                            />
                        }
                        label="Show people who have left"
                    />
                </Stack>

                {school && (
                    <>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }} flexWrap="wrap" useFlexGap>
                            <Chip label={`${admins} principal / admin`} color={admins ? 'primary' : 'default'} />
                            <Chip label={`${teachers} teacher${teachers === 1 ? '' : 's'}`} />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`database: ${school.db_status || 'not built'}`}
                                color={school.db_status === 'active' ? 'success' : 'warning'}
                            />
                        </Stack>

                        {notServing && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <AlertTitle>{school.code} is not serving yet</AlertTitle>
                                Its database is <b>{school.db_status || 'not built'}</b>. People can
                                only be added to a school that is active &mdash; provision it from
                                the Schools screen first.
                            </Alert>
                        )}

                        {admins === 0 && !notServing && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <AlertTitle>Nobody can sign in to {school.code}</AlertTitle>
                                This school has no administrator. Add the principal below; they can
                                then add their own teachers, sections and roster.
                            </Alert>
                        )}

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                disabled={notServing}
                                onClick={() => {
                                    setFormError(null);
                                    setForm(emptyForm('admin'));
                                }}
                            >
                                Add principal / admin
                            </Button>
                            <Button
                                variant="outlined"
                                disabled={notServing}
                                onClick={() => {
                                    setFormError(null);
                                    setForm(emptyForm('teacher'));
                                }}
                            >
                                Add teacher
                            </Button>
                        </Stack>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Login</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Added</TableCell>
                                        <TableCell align="right">&nbsp;</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <CircularProgress size={22} />
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!loading && members.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No staff yet.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!loading &&
                                        members.map(m => (
                                            <TableRow key={m.user_id} hover>
                                                <TableCell>{m.full_name}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                                    {m.username}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={m.role_label}
                                                        color={m.role === 'admin' ? 'primary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        label={m.status}
                                                        color={m.status === 'active' ? 'success' : 'default'}
                                                    />
                                                    {m.must_change_password && m.status === 'active' && (
                                                        <Tooltip title="They have not chosen their own password yet">
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                label="password not changed"
                                                                sx={{ ml: 0.5 }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                                <TableCell>{showTime(m.joined_at)}</TableCell>
                                                <TableCell align="right">
                                                    {m.status === 'active' && (
                                                        <>
                                                            <Button size="small" onClick={() => resetPassword(m)}>
                                                                Reset password
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                onClick={() => setConfirmRemove(m)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </CardContent>

            {/* --- add --------------------------------------------------- */}
            {form && (
                <Dialog open onClose={() => !saving && setForm(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {form.role === 'admin' ? 'Add a principal / administrator' : 'Add a teacher'}
                        {school ? ` to ${school.code}` : ''}
                    </DialogTitle>
                    <DialogContent>
                        {formError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {formError}
                            </Alert>
                        )}

                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                select
                                label="Role"
                                value={form.role}
                                onChange={e =>
                                    setForm({ ...form, role: e.target.value as 'admin' | 'teacher' })
                                }
                            >
                                <MenuItem value="admin">
                                    Principal / administrator &mdash; runs the school
                                </MenuItem>
                                <MenuItem value="teacher">
                                    Teacher &mdash; sees only their assigned sections
                                </MenuItem>
                            </TextField>

                            <TextField
                                label="Login (email address)"
                                value={form.username}
                                onChange={e => setForm({ ...form, username: e.target.value })}
                                required
                                autoFocus
                                // user.username is VARCHAR(50). Saying so here is
                                // kinder than the API saying it after a submit.
                                inputProps={{ maxLength: 50 }}
                                helperText="This is what they sign in with. It cannot be changed afterwards. Max 50 characters."
                            />

                            <TextField
                                label="Full name"
                                value={form.full_name}
                                onChange={e => setForm({ ...form, full_name: e.target.value })}
                                required
                            />

                            <TextField
                                label="Password"
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                helperText="Leave blank and one is generated. Either way they must change it at their first sign-in."
                            />

                            {form.role === 'teacher' && (
                                <>
                                    <TextField
                                        label="Employee number"
                                        value={form.employee_no}
                                        onChange={e => setForm({ ...form, employee_no: e.target.value })}
                                    />
                                    <TextField
                                        label="Contact email"
                                        value={form.email}
                                        onChange={e => setForm({ ...form, email: e.target.value })}
                                        helperText="Optional. Defaults to the login."
                                    />
                                    <TextField
                                        label="Phone"
                                        value={form.phone}
                                        onChange={e => setForm({ ...form, phone: e.target.value })}
                                    />
                                    <Alert severity="info">
                                        A teacher sees nothing until they are given sections. The
                                        school's principal does that from their own Teachers screen.
                                    </Alert>
                                </>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setForm(null)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={save} disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Add'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* --- remove ------------------------------------------------ */}
            {confirmRemove && (
                <Dialog open onClose={() => setConfirmRemove(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Remove {confirmRemove.full_name}?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Their membership of {school?.code} ends and every live session is
                            revoked immediately. The person and their history are kept, so this can
                            be undone by adding them again.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={() => remove(confirmRemove)}>
                            Remove
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {secret && (
                <PasswordOnce
                    username={secret.username}
                    password={secret.password}
                    onClose={() => setSecret(null)}
                />
            )}
        </Card>
    );
};

export default SchoolMembers;
